import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeaderboard } from '../lib/api';
import { Trophy, ArrowLeft, Crown, Medal, Award } from 'lucide-react';
import Marquee from 'react-fast-marquee';

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const { data } = await getLeaderboard();
      setLeaders(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={20} className="text-[#FFF200] drop-shadow-[0_0_8px_rgba(255,242,0,0.8)]" />;
    if (rank === 2) return <Medal size={20} className="text-[#C0C0C0] drop-shadow-[0_0_6px_rgba(192,192,192,0.6)]" />;
    if (rank === 3) return <Award size={20} className="text-[#CD7F32] drop-shadow-[0_0_6px_rgba(205,127,50,0.6)]" />;
    return <span className="font-mono text-sm text-[#71717A] w-5 text-center">{rank}</span>;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return '#FFF200';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    return '#A1A1AA';
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="bg-[#121214] border-b-2 border-[#FFF200]/30 p-4 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => navigate('/dashboard')} className="text-[#FFF200] hover:text-yellow-300 transition-colors">
          <ArrowLeft size={24} />
        </button>
        <Trophy className="text-[#FFF200] drop-shadow-[0_0_8px_rgba(255,242,0,0.8)]" size={24} />
        <h1 className="font-heading text-2xl uppercase tracking-wider text-[#FFF200] drop-shadow-[0_0_8px_rgba(255,242,0,0.5)]">
          Hall of Fame
        </h1>
      </div>

      {/* Marquee */}
      <div className="bg-black border-b border-gray-800 py-1">
        <Marquee speed={50} gradient={false}>
          <span className="font-mono text-[#FFF200] text-xs mx-8">
            NEW HIGH SCORE &bull; TOP PLAYERS &bull; HALL OF FAME &bull; CAN YOU BEAT THEM? &bull;
          </span>
        </Marquee>
      </div>

      <div className="max-w-md mx-auto p-4">
        {loading ? (
          <div className="text-center font-mono text-[#FFF200] animate-pulse py-8">LOADING SCORES...</div>
        ) : leaders.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="text-[#71717A] mx-auto mb-4" size={48} />
            <p className="font-mono text-[#71717A] text-lg">Aucun score enregistré</p>
            <p className="font-body text-sm text-[#71717A] mt-2">Soyez le premier !</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaders.map((entry) => (
              <div
                key={entry.rank}
                data-testid={`rank-${entry.rank}`}
                className={`bg-[#121214] border p-3 flex items-center gap-3 transition-all ${
                  entry.rank <= 3
                    ? 'border-[#FFF200]/30 shadow-[inset_0_0_15px_rgba(255,242,0,0.05)]'
                    : 'border-gray-800'
                }`}
              >
                <div className="w-8 flex items-center justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm uppercase tracking-wide" style={{ color: getRankColor(entry.rank) }}>
                      {entry.name}
                    </span>
                    <span className="font-mono text-xs text-[#71717A]">LVL {entry.level}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="font-mono text-xs text-[#A1A1AA]">{entry.total_sessions} games</span>
                    <span className="font-mono text-xs text-[#00AEEF]">avg {entry.avg_score}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-2xl" style={{ color: getRankColor(entry.rank), filter: `drop-shadow(0 0 8px ${getRankColor(entry.rank)})` }}>
                    {entry.best_score}
                  </div>
                  <div className="font-mono text-xs text-[#71717A]">BEST</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
